import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DunningService} from '../../../service/api/dunning.service';
import {BdDunningDto} from '../../../model/dto/api/bd-dunning.dto';
import {BdManualContractDecisionDto} from '../../../model/dto/api/bd-manual-contract-decision.dto';
import {extractError} from '../../../util/error-extraction.util';

/**
 * Auswahl-Option fuer den Abschreibungsgrund im Write-Off-Dialog.
 */
interface BdWriteOffReasonOption {
    label: string;
    value: string;
}

/**
 * AI:
 * Status: 'generated'
 * Type: Component
 * SubType: Exit-UI / Write-Off-Dialog
 * Reason: Pending-Action-Dialog fuer die Abschreibung einer Forderung. Erfasst
 *         den Abschreibungsgrund (Pflicht), zeigt den aktuellen Saldo read-only
 *         an und verlangt eine Freitext-Begruendung. Bei Bestaetigung wird
 *         {@link DunningService.decideManualContractDecision} mit {@code action='WRITE_OFF'}
 *         und der zusammengesetzten Begruendung aufgerufen.
 *
 *         Mapped to {@link BdDunningExitUiComponent.WRITE_OFF_DIALOG}.
 *
 * @ticket TM-1000001782
 */
@Component({
    selector: 'mvs-bd-write-off-dialog',
    templateUrl: './bd-write-off-dialog.component.html',
    styleUrls: ['./bd-write-off-dialog.component.scss'],
    standalone: false
})
export class BdWriteOffDialogComponent implements OnInit {

    // ------------------------------------------------------------------ //
    // Inputs
    // ------------------------------------------------------------------ //

    @Input() dunning: BdDunningDto | null = null;
    @Input() uiComponentConfig: string | null = null;

    // U17: Vom Drawer (ngComponentOutletInputs) durchgereichte Felder. Nicht
    // direkt verwendet, aber als @Input() deklariert, damit die Drawer-Bindung
    // kein NG0303 auslöst.
    @Input() dunningId: number | null = null;
    @Input() contracts: unknown[] | null = null;
    @Input() handOvers: unknown[] | null = null;
    @Input() activePause: unknown | null = null;
    @Input() breachedPlan: unknown | null = null;
    @Input() currentUserId: number | null = null;

    // ------------------------------------------------------------------ //
    // Outputs
    // ------------------------------------------------------------------ //

    @Output() actionCompleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() actionCancelled: EventEmitter<void> = new EventEmitter<void>();

    // ------------------------------------------------------------------ //
    // State
    // ------------------------------------------------------------------ //

    initialized: boolean = false;
    visible: boolean = true;
    submitting: boolean = false;
    errorMessage: string | null = null;

    /** Anzeige-Felder fuer den Mahnungs-Header. */
    customerName: string = '—';
    dunningTypeName: string = '—';
    activeLevelLabel: string = '—';

    /** Read-only Anzeige des aktuellen Saldos. */
    balanceLabel: string = '—';

    /** Vom Benutzer ausgewaehlter Abschreibungsgrund. */
    selectedReason: string | null = null;

    /** Pflicht-Begruendungstext aus dem Textarea. */
    comment: string = '';

    /** Auswahl-Optionen fuer den Abschreibungsgrund-Dropdown. */
    reasonOptions: BdWriteOffReasonOption[] = [
        {label: 'Geringer Wert',  value: 'GERINGER_WERT'},
        {label: 'Insolvenz',      value: 'INSOLVENZ'},
        {label: 'Verjährung',     value: 'VERJAEHRUNG'},
        {label: 'Unzustellbar',   value: 'UNZUSTELLBAR'},
        {label: 'Sonstiges',      value: 'SONSTIGES'}
    ];

    constructor(private readonly dunningService: DunningService) {
    }

    // ------------------------------------------------------------------ //
    // Lifecycle
    // ------------------------------------------------------------------ //

    ngOnInit(): void {
        this.initComponent();
    }

    private initComponent(): void {
        this.customerName = this.buildCustomerName(this.dunning);
        this.dunningTypeName = this.readString(this.dunning, ['dunningTypeName', 'typeName']) ?? '—';
        this.activeLevelLabel = this.readString(this.dunning, ['activeLevelShortName', 'activeLevelName']) ?? '—';
        this.balanceLabel = this.buildBalanceLabel(this.dunning);
        this.initialized = true;
    }

    // ------------------------------------------------------------------ //
    // Actions
    // ------------------------------------------------------------------ //

    /**
     * Sendet die Abschreibungs-Entscheidung ans Backend.
     */
    confirm(): void {
        if (this.selectedReason == null) {
            this.errorMessage = 'Bitte einen Abschreibungsgrund auswählen.';
            return;
        }
        const trimmedComment = this.comment?.trim() ?? '';
        if (trimmedComment.length === 0) {
            this.errorMessage = 'Bitte eine Begründung angeben.';
            return;
        }

        const dunningId = this.resolveDunningId();
        if (dunningId == null) {
            this.errorMessage = 'Keine Mahnungs-ID verfügbar.';
            return;
        }

        const decision: BdManualContractDecisionDto = {
            action: 'WRITE_OFF',
            comment: `[${this.selectedReason}] ${trimmedComment}`
        };

        this.errorMessage = null;
        this.submitting = true;

        this.dunningService.decideManualContractDecision(dunningId, decision).subscribe({
            next: () => {
                this.submitting = false;
                this.closeDialog();
                this.actionCompleted.emit();
            },
            error: (err: unknown) => {
                this.submitting = false;
                this.errorMessage = extractError(err);
            }
        });
    }

    onCancel(): void {
        this.closeDialog();
        this.actionCancelled.emit();
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.actionCancelled.emit();
        }
    }

    // ------------------------------------------------------------------ //
    // Helpers
    // ------------------------------------------------------------------ //

    private closeDialog(): void {
        this.visible = false;
    }

    private resolveDunningId(): number | null {
        if (!this.dunning) {
            return null;
        }
        const id = (this.dunning.id ?? this.dunning['dunningId']) as unknown;
        return typeof id === 'number' && id > 0 ? id : null;
    }

    private buildCustomerName(dunning: BdDunningDto | null): string {
        if (!dunning) {
            return '—';
        }
        const first = (dunning.personFirstName ?? dunning.firstName ?? '') as string;
        const last = (dunning.personLastName ?? dunning.lastName ?? '') as string;
        const combined = `${first} ${last}`.trim();
        return combined.length > 0 ? combined : '—';
    }

    private buildBalanceLabel(dunning: BdDunningDto | null): string {
        if (!dunning) {
            return '—';
        }
        const candidate = (
            dunning['balanceOnStart']
            ?? dunning['currentBalance']
            ?? dunning['balance']
        ) as unknown;
        if (typeof candidate === 'number') {
            return candidate.toLocaleString('de-DE', {
                style: 'currency',
                currency: 'EUR'
            });
        }
        if (typeof candidate === 'string' && candidate.length > 0) {
            return candidate;
        }
        return '—';
    }

    private readString(source: BdDunningDto | null, keys: string[]): string | null {
        if (!source) {
            return null;
        }
        for (const key of keys) {
            const value = source[key];
            if (typeof value === 'string' && value.length > 0) {
                return value;
            }
        }
        return null;
    }
}
